import { AsyncPipe, DatePipe } from '@angular/common'
import { Component, Input, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { Router } from '@angular/router'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderSearchInputDirective,
  ElderSearchPanelComponent,
  ElderSinglePaneWrapperComponent,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarColumnDirective,
  ElderToolbarContentDirective,
  Filter,
  IDataContext,
  Sort,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { FacilityContextMenuComponent } from '../../../context/facility-context/facility-context-menu/facility-context-menu.component'
import { FilterBuilder } from '../../../orders/filter-builder'
import { WorkStationsIndicatorComponent } from '../../workstations/work-stations-indicator/work-stations-indicator.component'
import { ControlCenter, ControlCenterType } from '../control-center'
import { ControlCenterFilter, ControlCenterService } from '../control-center.service'

@Component({
  selector: 'app-control-center-browser',
  templateUrl: './control-center-browser.component.html',
  styleUrls: ['./control-center-browser.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderToolbarColumnDirective,
    FacilityContextMenuComponent,
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    ElderSearchPanelComponent,
    MatFormField,
    MatInput,
    FormsModule,
    ElderSearchInputDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    WorkStationsIndicatorComponent,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
  ],
})
export class ControlCenterBrowserComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly data: IDataContext<ControlCenter>
  public readonly forcedFiltersByKey$ = new BehaviorSubject<Map<string, Filter>>(null)

  public readonly ControlCenterType = ControlCenterType

  /**
   * If set, indicates that the browser is scoped to
   * a specific type of control center.
   */
  public typeMode: ControlCenterType
  private router = inject(Router)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const controlCenterService = inject(ControlCenterService)
    this.data = DataContextBuilder.start<ControlCenter>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll(
        [FilterBuilder.FACILITY_KEY, ControlCenterFilter.TYPES_KEY],
        [new Sort('number', 'asc')]
      )
      .build(controlCenterService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input({ required: true })
  public set facility(facilityId: string) {
    if (facilityId) {
      const facilityFilter = FilterBuilder.facility(facilityId)
      const current = new Map(this.forcedFiltersByKey$.value) ?? new Map<string, Filter>()
      this.forcedFiltersByKey$.next(current.set(FilterBuilder.FACILITY_KEY, facilityFilter))
    }
  }

  @Input({ required: true })
  public set type(type: ControlCenterType) {
    this.typeMode = type
    if (type) {
      const controlCenterFilter = ControlCenterFilter.types(type)
      const current = new Map(this.forcedFiltersByKey$.value) ?? new Map<string, Filter>()
      this.forcedFiltersByKey$.next(current.set(ControlCenterFilter.TYPES_KEY, controlCenterFilter))
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openSideDetail(controlCenter: ControlCenter): void {
    this.router.navigate(
      [{ outlets: { side: ['shop-floor', 'control-centers', controlCenter.id] } }],
      { preserveFragment: true }
    )
  }

  public openDetail(controlCenter: ControlCenter): void {
    if (controlCenter) {
      this.router.navigate(['app', 'shop-floor', 'control-centers', controlCenter.id])
    }
  }

  public create(): void {
    this.router.navigate([{ outlets: { side: ['shop-floor', 'control-centers', 'new'] } }], {
      preserveFragment: true,
    })
  }

  public extractFilters(map: Map<string, Filter> | null): Array<Filter> | null {
    return !map ? null : [...map.values()]
  }
}
